import { GridHookForm } from 'vnetwork-ui'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import {
    PROCESS_FEEDBACK_STATUS,
    STORGE_PREFIXS,
} from '@/modules/Contract/modules/Process/constants'
import { createContractProcessHistory } from '@/modules/Contract/modules/Process/services'
import { CreateProcessResolveParams } from '@/modules/Contract/modules/Process/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

import { schemaCreateFeedback } from './schema'
import { renderFileItem } from '@/utils/function'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

export interface ResolveModalProps extends ModalProps {
    contractID: number
    contractProcessID: number
    contractNumberID: string
}

const CreateSubcontractModal = ({
    onClose,
    open,
    contractID,
    contractProcessID,
}: ResolveModalProps) => {
    const { uploadAsync, isUploading } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_PROCESS,
    })
    const { t } = useTranslation()

    const { refetch: callbackProcess } = useQueryClientCallback({
        pattern: 'processes',
        mode: 'contains',
    })
    const { refetch: callbackContract } = useQueryClientCallback({
        pattern: 'customers.contracts',
        mode: 'contains',
    })

    const formControl = useForm<CreateProcessResolveParams>({
        defaultValues: {
            process_feedback: '',
            process_feedback_status: 'in-progress',
        },
        resolver: yupResolver(schemaCreateFeedback as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createContractProcessHistory,
        },
        {
            successMessage: t('resolve-process-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.PROCESS_WITH_CONTRACT,
            onSuccess: () => {
                onClose()
                callbackProcess()
                callbackContract()
            },
        }
    )

    const handleCreate = async (values: CreateProcessResolveParams) => {
        let attachments: Attachment[] = []
        if (values?.attachments) {
            const attachmentUploadeds = await uploadAsync(values?.attachments)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        mutateAsync({
            contractID,
            contractProcessID,
            data: {
                ...values,
                attachments: attachments,
            },
        })
    }
    return (
        <Dialog
            title={t('contract-process-resolve')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending || isUploading,
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending || isUploading,
                        disabled: isPending || isUploading,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'process_feedback_status',
                            type: 'select',
                            label: t('process_feedback_status'),
                            placeholder: t('process_feedback_status'),
                            options: PROCESS_FEEDBACK_STATUS(t),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'process_feedback',
                            type: 'textarea',
                            label: t('process_feedback'),
                            placeholder: t('process_feedback'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            renderFileItem: renderFileItem,
                            label: t('attachments'),
                            multiple: true,
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateSubcontractModal
